import { useState, useEffect, useCallback } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [ledger, setLedger] = useState(null);
  const [ledgerUser, setLedgerUser] = useState(null);

  const load = useCallback((query = "") => {
    const seq = ++load.seq;
    adminApi.get(`/admin/users?q=${encodeURIComponent(query)}`)
      .then(({ data }) => { if (seq === load.seq) setUsers(data.users); })
      .catch(() => { if (seq === load.seq) setUsers([]); });
  }, []);
  load.seq = load.seq || 0;

  useEffect(() => { load(); }, [load]);

  const adjust = async (u) => {
    const amount = window.prompt(`Adjust coins for ${u.name || u.phone} (use negative to deduct):`);
    if (!amount || isNaN(Number(amount))) return;
    const reason = window.prompt("Reason for adjustment:") || "manual adjustment";
    try {
      const { data } = await adminApi.post(`/admin/users/${u.id}/adjust-coins`, { amount: Number(amount), reason });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, coins: data.coins } : x)));
      toast.success(`Balance updated: ${data.coins.toLocaleString()} coins`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const ban = async (u) => {
    try {
      const { data } = await adminApi.post(`/admin/users/${u.id}/ban`);
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, is_banned: data.is_banned } : x)));
      toast.success(data.is_banned ? "User banned" : "User unbanned");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const grantTier = async (u, tier) => {
    try {
      const { data } = await adminApi.post(`/admin/users/${u.id}/tier`, { tier: tier || null });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, tier: data.tier } : x)));
      toast.success(data.tier ? `${u.name || u.phone} is now ${data.tier} (30 days)` : "Ladder level removed");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const viewLedger = async (u) => {
    setLedgerUser(u);
    const { data } = await adminApi.get(`/admin/users/${u.id}/ledger`);
    setLedger(data.transactions);
  };

  return (
    <div data-testid="admin-users">
      <h1 className="text-xl font-extrabold font-display mb-6">User Management</h1>
      <div className="flex items-center gap-2 bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 mb-4 max-w-sm">
        <Search size={14} className="text-zinc-500" />
        <input data-testid="user-search-input" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder="Search name, username or phone…" className="bg-transparent outline-none flex-1 text-sm text-white placeholder:text-zinc-600" />
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 text-left border-b border-white/10">
              <th className="p-3">User</th><th>Phone</th><th>Level</th><th>Coins</th><th>Rubies</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users?.map((u) => (
              <tr key={u.id} className={`border-b border-white/5 ${u.is_banned ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="p-3 flex items-center gap-2">
                  <img src={u.avatar || "https://i.pravatar.cc/100?u=x"} alt="" className="w-7 h-7 rounded-full object-cover" />
                  <div>
                    <p className="text-white font-semibold">{u.name || "—"}</p>
                    <p className="text-zinc-500 text-[10px]">@{u.username || "not set"}</p>
                  </div>
                </td>
                <td className="text-zinc-400">{u.phone}</td>
                <td className="text-zinc-300">Lv.{u.level} · {u.tier}</td>
                <td className="text-amber-400 font-bold">{u.coins?.toLocaleString()}</td>
                <td className="text-cyan-300 font-semibold">{u.diamonds?.toLocaleString()}</td>
                <td className="pr-3 space-x-1.5 whitespace-nowrap">
                  <select data-testid={`user-tier-${u.id}`} value={u.tier || ""} onChange={(e) => grantTier(u, e.target.value)}
                    className="bg-white/10 px-1.5 py-1 rounded-lg text-zinc-300 font-semibold outline-none text-[10px] max-w-[92px]">
                    <option value="">No level</option>
                    {["Friendship", "Star", "Silver", "Gold", "Pearl", "Ruby", "Emerald", "Sapphire", "Diamond", "Black Diamond", "Kohinoor", "Maharaja", "Shahenshah"].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <button data-testid={`user-adjust-${u.id}`} onClick={() => adjust(u)} className="bg-white/10 px-2.5 py-1 rounded-lg text-zinc-300 font-semibold hover:bg-white/20">Adjust</button>
                  <button data-testid={`user-ledger-${u.id}`} onClick={() => viewLedger(u)} className="bg-white/10 px-2.5 py-1 rounded-lg text-zinc-300 font-semibold hover:bg-white/20">Ledger</button>
                  <button data-testid={`user-ban-${u.id}`} onClick={() => ban(u)} className={`px-2.5 py-1 rounded-lg font-semibold ${u.is_banned ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                    {u.is_banned ? "Unban" : "Ban"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ledgerUser && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setLedgerUser(null)}>
          <div className="glass bg-[#14141c] rounded-3xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-bold font-display mb-4">Ledger — {ledgerUser.name || ledgerUser.phone}</h2>
            {ledger === null && <p className="text-zinc-500 text-xs">Loading…</p>}
            {ledger?.length === 0 && <p className="text-zinc-500 text-xs">No transactions</p>}
            <div className="space-y-1.5">
              {ledger?.map((t) => (
                <div key={t.id} className="flex justify-between text-xs bg-white/5 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-white font-semibold">{t.type}</p>
                    <p className="text-zinc-500 text-[10px]">{t.meta?.reason || t.meta?.gift || ""} · {new Date(t.created_at).toLocaleString()}</p>
                  </div>
                  <span className={t.amount >= 0 ? "text-emerald-400 font-bold" : "text-rose-400 font-bold"}>{t.amount.toLocaleString()} {t.currency}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
